import { AlertBanner } from '@/components/common/AlertBanner'
import { ApplicationFooter } from '@/components/common/ApplicationFooter'
import { Navbar } from '@/components/Navbar'
import { NewOrderModal, OrderFormData } from '@/components/NewOrderModal'
import { useWorkspaceFilters } from '@/components/orders/useWorkspaceFilters'
import { WorkspaceOrdersDataTable } from '@/components/orders/WorkspaceOrdersDataTable'
import { clearDiscardMarker } from '@/features/ordering/discardMarker'
import { resolveDiscardNavMessage, type DiscardNavState } from '@/pages/orders/discardNavMessage'
import { getClientName, getOrganizationSiteNames, isOrderSiteTrackingEnabled } from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'
import { useAuthorization } from '@/features/authorization'
import { DelegationSelector } from '@/features/authorization/DelegationSelector'
import { selectFinalGuidanceReport } from '@/features/report/selectFinalGuidanceReport'
import { getAllOrderReports } from '@/features/report/services/reportClient'
import { legacyTrfPayloadToCanonical } from '@/ordering/canonicalOrder'
import { useAppDispatch } from '@/store/hooks'
import { resetCanonicalOrder, setCanonicalOrder } from '@/store/slices/canonicalOrderSlice'
import { DEFAULT_CONSENT_DATA } from '@/types/RequisitionFormTypes'
import {
  getOrderById,
  OrderStatus,
  OrderStatusResponse,
  searchWorkspaceOrders,
  type WorkspaceSearchQuery,
} from '@/utils/DatabaseOperations'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import { motion } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * Orders dashboard for standalone flow
 * Displays order statistics and list of patient orders
 */
const Orders: React.FC = () => {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently, logout } = useAuth0()
  const {
    permissions,
    roles,
    orgId,
    isLoading: permissionsLoading,
    activeDelegation,
    availableDelegations,
  } = useAuthorization()
  const { status: configStatus, config } = useUiConfig()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const [isNewOrderModalOpen, setIsNewOrderModalOpen] = useState(false)

  useEffect(() => {
    const navState = location.state as { openNewOrder?: boolean } | null
    if (navState?.openNewOrder) {
      setIsNewOrderModalOpen(true)
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [location.pathname, location.state, navigate])

  // Orders state - replace mock data with real API data
  const [orders, setOrders] = useState<OrderStatusResponse[]>([])
  const [ordersTotal, setOrdersTotal] = useState(0)
  const [statusCounts, setStatusCounts] = useState<{ counts: Record<string, number>; search: string }>({
    counts: {},
    search: '',
  })
  // True only until the first page arrives; later refetches keep the current rows on screen.
  const [ordersLoading, setOrdersLoading] = useState(true)
  const [ordersError, setOrdersError] = useState<string | null>(null)
  const ordersRequestIdRef = useRef(0)
  const ordersClearedRef = useRef(false)
  const workspaceFilters = useWorkspaceFilters(statusCounts.counts, ordersTotal, statusCounts.search)
  const [discardToast, setDiscardToast] = useState<string | null>(null)

  useEffect(() => {
    const message = resolveDiscardNavMessage(location.state as DiscardNavState)
    if (message) {
      setDiscardToast(message)
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [location.pathname, location.state, navigate])

  // Check if user is a delegate
  const isDelegate = roles.includes('delegate')

  // Helper function to parse provider name from Auth0 user
  const getProviderNames = () => {
    // Try given_name and family_name first (standard Auth0 fields)
    if (user?.given_name && user?.family_name) {
      return {
        firstName: user.given_name,
        lastName: user.family_name,
        fullName: `${user.given_name} ${user.family_name}`,
      }
    }

    // Parse from name field (e.g., "Eric Grande")
    if (user?.name && user.name !== user?.email) {
      const nameParts = user.name.trim().split(/\s+/)
      if (nameParts.length >= 2) {
        return {
          firstName: nameParts[0],
          lastName: nameParts.slice(1).join(' '),
          fullName: user.name,
        }
      } else if (nameParts.length === 1) {
        return {
          firstName: nameParts[0],
          lastName: '',
          fullName: user.name,
        }
      }
    }

    // Fallback to nickname or email username
    const fallbackName = user?.nickname || user?.email?.split('@')[0] || 'Unknown'
    return {
      firstName: fallbackName,
      lastName: 'Provider',
      fullName: `${fallbackName} Provider`,
    }
  }

  const providerNames = getProviderNames()

  const handleNewOrder = () => {
    Sentry.startSpan(
      {
        op: 'ui.click',
        name: 'New Order Button',
      },
      (span) => {
        span.setAttribute('auth.flow', 'standalone')
        setIsNewOrderModalOpen(true)
      }
    )
  }

  const handleCreateOrder = async (orderData: OrderFormData) => {
    await Sentry.startSpan(
      {
        op: 'order.create',
        name: 'Create New Order',
      },
      async (span) => {
        span.setAttribute('patient.lastName', orderData.patientLastName)
        span.setAttribute('patient.firstName', orderData.patientFirstName)
        span.setAttribute('patient.mrn', orderData.mrn)

        // Use the IMDB org UUID from the JWT (resolved by useAuthorization for the
        // standalone flow that this page runs under). This is the same key the
        // microapp uses as its source-of-truth identifier.
        const lookupOrgKey = orgId || ''

        let organizationName = 'Inflection Medicine'
        let state = ''
        let country = ''
        if (lookupOrgKey) {
          try {
            const token = await getAccessTokenSilently()
            const response = await fetch(`${import.meta.env['VITE_PROVIDER_API_URL']}/config/ui/${lookupOrgKey}`, {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
              },
            })

            if (response.ok) {
              const configData = await response.json()
              organizationName = configData.client_name || organizationName
              state = configData.state || ''
              country = configData.country || 'USA'

              span.setAttribute('organization.name', organizationName)
              span.setAttribute('organization.state', state)
            } else {
              const errorText = await response.text()
              console.error('Failed to fetch client config:', response.status, response.statusText, errorText)
              Sentry.captureMessage('Failed to fetch client config', {
                level: 'warning',
                extra: { orgId: lookupOrgKey, status: response.status, error: errorText },
              })
            }
          } catch (error) {
            console.error('Error fetching client config:', error)
            Sentry.captureException(error, {
              tags: { operation: 'fetch_client_config' },
              extra: { orgId: lookupOrgKey },
            })
          }
        } else {
          console.log('[Orders] No orgId in JWT, using defaults')
        }
        

        // Create OrderFormInfoData from the modal form data
        // This will be passed via location.state to OrderStart
        const patientOrderData = {
          patient: {
            id: `patient-${Date.now()}`, // Generate temporary ID for standalone flow
            name: {
              firstName: orderData.patientFirstName,
              lastName: orderData.patientLastName,
            },
            mrn: orderData.mrn,
            sex: orderData.sex,
            birthDate: orderData.dateOfBirth ? new Date(orderData.dateOfBirth) : null,
          },
          orderingProvider: orderData.orderingPhysician
            ? {
                id: user?.sub || user?.email || `provider-${Date.now()}`, // Use Auth0 sub (user ID) or email as provider ID
                name: {
                  firstName: providerNames.firstName,
                  lastName: providerNames.lastName,
                },
                npi: (user?.['https://provider.dev.inflectionmedicine.com/npi'] as string) || '',
              }
            : undefined,
          medicalProfessionalInfo: {
            npi: (user?.['https://provider.dev.inflectionmedicine.com/npi'] as string) || '',
            name: providerNames.fullName,
            firstName: providerNames.firstName,
            lastName: providerNames.lastName,
            email: user?.email,
          },
          institution: {
            id: lookupOrgKey,
            name: organizationName,
            address: {
              street: '',
              city: '',
              state,
              zip: '',
              country,
            },
          },
          organizationId: lookupOrgKey,
          organizationName,
          providerType: 'physician',
          orderDate: new Date(),
          // Add consent data - required by IMDB backend
          consent: {
            ...DEFAULT_CONSENT_DATA,
            providerName: providerNames.fullName,
            providerEmail: user?.email || '',
          },
        }

        // Close modal and navigate to order/start with patient data
        setIsNewOrderModalOpen(false)

        // Clear any existing order state before starting a new order
        dispatch(resetCanonicalOrder())

        // A deliberate new order lifts any discard suppression for this patient.
        clearDiscardMarker(patientOrderData.patient.id)

        navigate('/order/start', {
          state: {
            patientData: patientOrderData,
            authFlow: 'standalone',
            // GEN-5738: carry the ordering site chosen in the New Order modal so
            // OrderStart seeds it into the draft-create payload (required at
            // create for orgs that track ordering sites).
            orderingSite: orderData.orderingSite,
          },
        })
      }
    )
  }

  const handleLogout = () => {
    Sentry.startSpan(
      {
        op: 'ui.click',
        name: 'Logout Button',
      },
      (span) => {
        span.setAttribute('auth.flow', 'standalone')
        logout({
          logoutParams: {
            returnTo: window.location.origin + '/login',
          },
        })
      }
    )
  }

  /**
   * Navigate to the appropriate order route based on order status.
   * Mirrors the redirection logic in Callback.tsx (Epic/SMART flow).
   * - DRAFT / incomplete → /order/start (load existing form data if available)
   * - COMPLETE / results → /order/results/:orderId
   * - Otherwise (submitted, in progress) → /order/in-progress/:orderId/:patientId
   */
  const handleOrderClick = async (order: OrderStatusResponse) => {
    const orderId = order.order_id
    const patientId = order.patient_id || 'unknown-patient'
    const statusNormalized = order.status?.toLowerCase()
    const caller = `${providerNames.firstName || ''} ${providerNames.lastName || ''}`.trim() || 'spa'

    if (order.ordering_session_id) {
      const sid = order.ordering_session_id
      dispatch(resetCanonicalOrder())
      navigate(`/order/start?sessionId=${encodeURIComponent(sid)}`, {
        state: { authFlow: 'standalone' },
      })
      return
    }

    const isEditingPhase =
      statusNormalized === OrderStatus.DRAFT || statusNormalized === 'incomplete'
    const isResultReady =
      statusNormalized === OrderStatus.COMPLETE || statusNormalized === 'results'

    const patientDemographics = {
      firstName: order.patient_first_name,
      lastName: order.patient_last_name,
      dateOfBirth: order.patient_date_of_birth,
      sex: order.patient_sex,
      mrn: order.patient_mrn,
    }

    if (isEditingPhase) {
      dispatch(resetCanonicalOrder())
      try {
        const existingRequest = await getOrderById(orderId, caller)
        if (existingRequest?.json_payload?.requisitionFormData) {
          dispatch(setCanonicalOrder(legacyTrfPayloadToCanonical({
            orderFormInfo: existingRequest.json_payload.orderFormInfo,
            requisitionFormData: existingRequest.json_payload.requisitionFormData,
          })))
        }
        navigate('/order/start', {
          state: { orderId, authFlow: 'standalone' },
        })
      } catch (error) {
        console.error('[Orders] Failed to load draft order:', error)
        Sentry.captureException(error, {
          tags: { operation: 'orders.load_draft', orderId },
        })
        navigate(`/order/in-progress/${encodeURIComponent(orderId)}/${patientId}`, {
          state: { patientDemographics },
        })
      }
      return
    }

    if (isResultReady && orderId) {
      const resultsState = {
        orderId,
        backendStatus: order.status,
        patientDemographics,
      }
      // Route to the Guidance page only when the org has the guidance feature
      // enabled AND this order has a finalized guidance envelope
      // (guidance.status === 'final'); otherwise fall back to the PDF results.
      // The results page derives the "Next Steps excluded" notice itself when
      // guidance was rejected, so no extra state is needed here.
      let destination = `/order/results/${encodeURIComponent(orderId)}`
      if (config.featureFlags?.guidance === true) {
        try {
          const reports = await getAllOrderReports(orderId, caller)
          if (selectFinalGuidanceReport(reports)) {
            destination = `/order/guidance/${encodeURIComponent(orderId)}`
          }
        } catch (error) {
          Sentry.captureException(error, { tags: { operation: 'orders.guidance_check', orderId } })
        }
      }
      navigate(destination, { state: resultsState })
      return
    }

    // Submitted but not complete → in-progress (same as Callback.tsx else branch)
    navigate(`/order/in-progress/${encodeURIComponent(orderId)}/${patientId}`, {
      state: {
        orderId,
        backendStatus: order.status,
        patientDemographics,
      },
    })
  }

  useEffect(() => {
    Sentry.startSpan(
      {
        op: 'ui.view',
        name: 'Orders Page View',
      },
      (span) => {
        span.setAttribute('auth.flow', 'standalone')
        span.setAttribute('is_authenticated', isAuthenticated)
        span.setAttribute('config_status', configStatus)
      }
    )
  }, [isAuthenticated, configStatus])

  /**
   * Load one server page of the workspace (orders + open drafts). Search, status filter, sort and
   * paging run server-side; the server resolves org-wide (org_admin) vs. user scope. Users without
   * order:view get an empty table. Stale responses (superseded by a newer query) are ignored.
   */
  const queryKey = workspaceFilters.query === null ? null : JSON.stringify(workspaceFilters.query)
  useEffect(() => {
    if (!isAuthenticated || permissionsLoading || !orgId) {
      return
    }
    const requestId = ++ordersRequestIdRef.current
    const isCurrent = () => requestId === ordersRequestIdRef.current

    if (!permissions.includes('order:view')) {
      setOrders([])
      setOrdersTotal(0)
      setStatusCounts({ counts: {}, search: '' })
      setOrdersError(null)
      setOrdersLoading(false)
      return
    }
    if (queryKey === null) {
      // Every status deselected: nothing can match. Keep status counts so the options stay visible.
      setOrders([])
      setOrdersTotal(0)
      ordersClearedRef.current = true
      setOrdersError(null)
      setOrdersLoading(false)
      return
    }
    if (ordersClearedRef.current) {
      ordersClearedRef.current = false
      setOrdersLoading(true)
    }
    const query = JSON.parse(queryKey) as WorkspaceSearchQuery

    const loadOrders = async () => {
      try {
        setOrdersError(null)
        await Sentry.startSpan({ op: 'api.fetch', name: 'Load Orders' }, async (span) => {
          span.setAttribute('org_id', orgId)
          span.setAttribute('load_type', roles.includes('org_admin') ? 'organization' : 'user')
          span.setAttribute('delegation', activeDelegation ? 'active' : 'none')
          span.setAttribute('page', query.page)
          span.setAttribute('search_applied', query.search !== '')
          const result = await searchWorkspaceOrders(query, activeDelegation)
          span.setAttribute('orders_count', result.items.length)
          span.setAttribute('orders_total', result.total)
          if (!isCurrent()) return
          setOrders(result.items)
          setOrdersTotal(result.total)
          setStatusCounts({ counts: result.status_counts, search: query.search })
        })
      } catch (error) {
        if (!isCurrent()) return
        console.error('[Orders] Failed to load orders:', error instanceof Error ? error.name : 'unknown')
        setOrdersError(error instanceof Error ? error.message : 'Failed to load orders')
        Sentry.captureException(error, {
          tags: { operation: 'load_orders', flow: 'standalone' },
          extra: { orgId, roles, permissions },
        })
      } finally {
        if (isCurrent()) setOrdersLoading(false)
      }
    }

    loadOrders()
  }, [isAuthenticated, permissionsLoading, orgId, roles, permissions, activeDelegation, queryKey])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar
          userAvatar={user?.picture}
          userName={user?.name}
          onLogout={handleLogout}
          showNavLinks={true}
          organizationName={getClientName(config)}
          onCreateNewOrder={handleNewOrder}
        />
        <div className="flex min-h-[50vh] items-center justify-center px-4">
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-8 shadow-lg">
            <div className="text-center">
              <div
                className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              >
                <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                  Loading...
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-600">Loading authentication data...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="w-full max-w-md p-8">
          <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-lg">
            <div className="text-center">
              <h3 className="mb-2 text-lg font-medium text-gray-900">Not Authenticated</h3>
              <p className="mb-4 text-sm text-gray-600">Please log in to view this page.</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <Navbar
        userAvatar={user?.picture}
        userName={user?.name}
        onLogout={handleLogout}
        showNavLinks={true}
        organizationName={getClientName(config)}
        onCreateNewOrder={handleNewOrder}
      />

      {/* Delegation Selector (only shown for delegates) */}
      {isDelegate && availableDelegations.length > 0 && (
        <div className="border-b border-yellow-300 bg-yellow-50 px-4 py-3">
          <div className="mx-auto max-w-full">
            <DelegationSelector
              onDelegationChange={() => {
                // Orders will reload automatically via useEffect dependency
                console.log('[Orders] Delegation changed, orders will reload')
              }}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <motion.div
        className="mx-auto max-w-full px-4 py-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.24, ease: 'easeOut' }}
      >
        {/* Page Header */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-text-heading dark:text-white">Workspace</h1>
            <p className="mt-1 text-sm text-text-body-subtle dark:text-gray-400">
              Welcome back, {providerNames.firstName}.
            </p>
          </div>
          <button
            onClick={handleNewOrder}
            className="flex items-center gap-2 rounded-full bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-600/20 dark:focus:ring-offset-gray-900"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Start New Order
          </button>
        </div>

        {discardToast && (
          <AlertBanner onClose={() => setDiscardToast(null)} useInflectionIcon={true}>
            {discardToast}
          </AlertBanner>
        )}

        {/*
          IMPORTANT: Session sort/filter/page state lives in `useWorkspaceFilters` on this page so it
          survives refetches. Do NOT move it into a component keyed on refetch inputs
          (orgId / activeDelegation / roles / permissions) or otherwise remount its owner; that would
          silently reset the user's selections (violates AC#6).
        */}
        <WorkspaceOrdersDataTable
          orders={orders}
          filters={workspaceFilters}
          ordersLoading={ordersLoading}
          ordersError={ordersError}
          permissions={permissions}
          roles={roles}
          onOrderClick={handleOrderClick}
        />

        <ApplicationFooter className="mt-8 shrink-0 bg-background-gray pt-6" />
      </motion.div>

      {/* New Order Modal */}
      <NewOrderModal
        isOpen={isNewOrderModalOpen}
        onClose={() => setIsNewOrderModalOpen(false)}
        onSubmit={handleCreateOrder}
        userFirstName={providerNames.firstName}
        userLastName={providerNames.lastName}
        userEmail={user?.email}
        organizationId={orgId ?? ''}
        siteTrackingEnabled={isOrderSiteTrackingEnabled(config)}
        siteOptions={getOrganizationSiteNames(config)}
      />
    </div>
  )
}

export default Orders
